// client/src/coms/components/CreateServerModal.js
import React, { useMemo, useState } from 'react';
import ComsModal from './ComsModal';
import { joinByInvite } from '../e2ee/EncryptionConsentHost';
import { DiscordImportPanel } from '../organisation/DiscordImport';

function extractInviteCode(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';

  try {
    const url = new URL(s);
    const q = String(url.searchParams.get('invite') || '').trim();
    if (q) return q;
  } catch {}

  try {
    const qIndex = s.indexOf('?');
    if (qIndex >= 0) {
      const qs = s.slice(qIndex);
      const sp = new URLSearchParams(qs.startsWith('?') ? qs : `?${qs}`);
      const q = String(sp.get('invite') || '').trim();
      if (q) return q;
    }
  } catch {}

  return s;
}

function pickServerIdFromPayload(p) {
  const obj = p && typeof p === 'object' ? p : {};

  const direct =
    obj.server_key != null
      ? String(obj.server_key)
      : obj.public_id != null
        ? String(obj.public_id)
        : obj.server_id != null
          ? String(obj.server_id)
          : obj.serverId != null
            ? String(obj.serverId)
            : obj.id != null
              ? String(obj.id)
              : obj.server && obj.server.server_key != null
                ? String(obj.server.server_key)
                : obj.server && obj.server.public_id != null
                  ? String(obj.server.public_id)
                  : obj.server && obj.server.id != null
                    ? String(obj.server.id)
                    : obj.server && obj.server.server_id != null
                      ? String(obj.server.server_id)
                      : '';

  const out = String(direct || '').trim();
  if (out) return out;

  const list = Array.isArray(obj.servers) ? obj.servers : [];
  if (list.length) {
    const first = list[0] || {};
    const id =
      first.server_key != null
        ? String(first.server_key)
        : first.public_id != null
          ? String(first.public_id)
          : first.id != null
            ? String(first.id)
            : first.server_id != null
              ? String(first.server_id)
              : first.serverId != null
                ? String(first.serverId)
                : '';
    const out2 = String(id || '').trim();
    return out2 || '';
  }

  return '';
}

export default function CreateServerModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [inviteInput, setInviteInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const [showJoinNameModal, setShowJoinNameModal] = useState(false);
  const [pendingInviteCode, setPendingInviteCode] = useState('');
  const [joinDisplayName, setJoinDisplayName] = useState('');
  const [joinNameStepBusy, setJoinNameStepBusy] = useState(false);
  const [joinNameStepErr, setJoinNameStepErr] = useState('');
  const [discordOpen, setDiscordOpen] = useState(false);

  const inviteCode = useMemo(() => extractInviteCode(inviteInput), [inviteInput]);
  const isJoinMode = !!String(inviteCode || '').trim();

  const canCreate = String(name || '').trim().length >= 2;
  const canJoin = !!String(inviteCode || '').trim();
  const canSubmit = isJoinMode ? canJoin : canCreate;

  async function doJoinByInvite(code, displayName) {
    // Encrypted servers ask for explicit acceptance first (e2ee/EncryptionConsentHost.js).
    const data = await joinByInvite({ invite_code: code });

    const serverId = pickServerIdFromPayload(data);
    const nick = String(displayName || '').trim();

    if (nick && serverId) {
      const res2 = await fetch(`/api/coms/servers/${encodeURIComponent(serverId)}/me`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: nick }),
      });

      const data2 = await res2.json().catch(() => null);

      if (!res2.ok || (data2 && data2.error)) {
        throw new Error((data2 && data2.error) || 'Joined, but failed to set your server nickname.');
      }
    }

    return { data, serverId };
  }

  async function finishJoin(code, displayName) {
    const result = await doJoinByInvite(code, displayName);

    if (typeof onCreate === 'function') {
      try {
        await onCreate({
          mode: 'join',
          invite_code: code,
          serverId: result.serverId || '',
          data: result.data || null,
        });
      } catch {}
    }

    onClose();
  }

  function openJoinNameStep(code) {
    setPendingInviteCode(String(code || '').trim());
    setJoinDisplayName('');
    setJoinNameStepErr('');
    setShowJoinNameModal(true);
  }

  function closeJoinNameStep() {
    if (joinNameStepBusy) return;
    setShowJoinNameModal(false);
    setPendingInviteCode('');
    setJoinDisplayName('');
    setJoinNameStepErr('');
  }

  async function submit(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    if (!canSubmit || busy) return;

    setErr('');

    if (isJoinMode) {
      openJoinNameStep(inviteCode);
      return;
    }

    setBusy(true);

    try {
      const payload = {
        name: String(name || '').trim(),
      };

      await onCreate(payload);
      onClose();
    } catch (e2) {
      setErr(e2?.message || 'Failed to create server');
    } finally {
      setBusy(false);
    }
  }

  async function handleJoinNow() {
    const code = String(pendingInviteCode || '').trim();
    if (!code || joinNameStepBusy) return;

    setJoinNameStepBusy(true);
    setJoinNameStepErr('');
    setBusy(true);
    setErr('');

    try {
      await finishJoin(code, '');
    } catch (e) {
      setJoinNameStepErr(e?.message || 'Failed to join server');
      setBusy(false);
      setJoinNameStepBusy(false);
    }
  }

  async function handleJoinWithName() {
    const code = String(pendingInviteCode || '').trim();
    if (!code || joinNameStepBusy) return;

    setJoinNameStepBusy(true);
    setJoinNameStepErr('');
    setBusy(true);
    setErr('');

    try {
      await finishJoin(code, String(joinDisplayName || '').trim());
    } catch (e) {
      setJoinNameStepErr(e?.message || 'Failed to join server');
      setBusy(false);
      setJoinNameStepBusy(false);
    }
  }

  return (
    <>
      <ComsModal
        title="Create or join a server"
        onClose={onClose}
        footer={
          <>
            <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy || joinNameStepBusy}>
              Cancel
            </button>
            <button
              type="button"
              className="coms-mini-btn primary"
              onClick={submit}
              disabled={!canSubmit || busy || joinNameStepBusy}
            >
              {busy ? (isJoinMode ? 'Joining…' : 'Creating…') : isJoinMode ? 'Join' : 'Create'}
            </button>
          </>
        }
      >
        <form onSubmit={submit} className="coms-form">
          <label className="coms-form-row">
            <div className="coms-form-label">Name</div>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Point Atlas Creators"
              autoFocus={!isJoinMode}
              disabled={busy || joinNameStepBusy}
            />
            <div className="coms-form-hint">Leave the invite code blank to create a new server.</div>
          </label>

          <label className="coms-form-row">
            <div className="coms-form-label">Enter invite code (or invite URL)</div>
            <input
              value={inviteInput}
              onChange={(e) => setInviteInput(e.target.value)}
              placeholder="SERVERKEY:INVITECODE or https://.../comms/?invite=..."
              autoFocus={isJoinMode}
              disabled={busy || joinNameStepBusy}
            />
            <div className="coms-form-hint">
              Paste either the plain invite code or the full invite link. If this is filled in, the modal will join instead of create.
            </div>
          </label>

          {err ? <div className="coms-form-error">{err}</div> : null}

          <div className="coms-form-row">
            <button type="button" className="coms-mini-btn" onClick={() => setDiscordOpen(true)} disabled={busy || joinNameStepBusy} data-testid="create-from-discord">
              Import from a Discord template…
            </button>
            <div className="coms-form-hint">Start a new server with the categories, channels and roles of a Discord Server Template.</div>
          </div>
        </form>
      </ComsModal>

      {discordOpen ? (
        <ComsModal title="New server from a Discord template" onClose={() => setDiscordOpen(false)} maxWidth={720}>
          <DiscordImportPanel
            allowExisting={false}
            onCancel={() => setDiscordOpen(false)}
            onImported={(r) => {
              if (r && r.server_key) window.dispatchEvent(new CustomEvent('vox-open-server', { detail: { serverKey: r.server_key } }));
            }}
          />
        </ComsModal>
      ) : null}

      {showJoinNameModal ? (
        <ComsModal
          title="Join server"
          onClose={closeJoinNameStep}
          footer={
            <>
              <button type="button" className="coms-mini-btn" onClick={closeJoinNameStep} disabled={joinNameStepBusy}>
                Back
              </button>
              <button
                type="button"
                className="coms-mini-btn"
                onClick={handleJoinNow}
                disabled={joinNameStepBusy}
              >
                {joinNameStepBusy ? 'Joining…' : 'Join now'}
              </button>
              <button
                type="button"
                className="coms-mini-btn primary"
                onClick={handleJoinWithName}
                disabled={joinNameStepBusy}
              >
                {joinNameStepBusy ? 'Joining…' : 'Join with this name'}
              </button>
            </>
          }
        >
          <div className="coms-form">
            <div className="coms-form-row">
              <div className="coms-form-label">Invite code</div>
              <input value={pendingInviteCode} readOnly />
              <div className="coms-form-hint">
                You can join right now, or set a server-specific display name first.
              </div>
            </div>

            <label className="coms-form-row">
              <div className="coms-form-label">Your display name for this server (optional)</div>
              <input
                value={joinDisplayName}
                onChange={(e) => setJoinDisplayName(e.target.value)}
                placeholder="Leave blank to keep your current name"
                autoFocus
                disabled={joinNameStepBusy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleJoinWithName();
                  }
                }}
              />
              <div className="coms-form-hint">This sets your per-server nickname right after joining.</div>
            </label>

            {joinNameStepErr ? <div className="coms-form-error">{joinNameStepErr}</div> : null}
          </div>
        </ComsModal>
      ) : null}
    </>
  );
}